import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, Clock, DollarSign, User, Settings, Edit, Upload, 
  ToggleLeft, ToggleRight, CheckCircle, XCircle, AlertCircle, 
  Briefcase, MapPin, Globe, Mail, Phone, Award, Send, ChevronDown, 
  Loader2, LogOut, FileText, Wallet, TrendingUp, Languages, Monitor, Users
} from 'lucide-react';
import { counselingApi } from '../../services/counselingApi';
import { useTheme } from '../../Components/Home/ThemeContext'; 

const CounselorDashboard = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  
  // --- State Management ---
  const [activeTab, setActiveTab] = useState('overview');
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [accepting, setAccepting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [publicationStatus, setPublicationStatus] = useState(null);

  // Stats State
  const [earnings, setEarnings] = useState(0);
  const [completedSessions, setCompletedSessions] = useState(0);

  // --- Options Constants (Must match Registration/Filter) ---
  const pricingTypes = ["Per Hour", "Per Session", "Per Week", "Per Month", "One-time Fee", "Yearly Subscription"];
  const categoryOptions = ['Education Counsellor', 'Guidance Counsellor', 'Study Abroad Counsellor', 'Admission Counsellor', 'Student Career Counsellor', 'Mental Health Counsellor', 'Child Psychology Counsellor', 'Relationship Counsellor', 'Career Counsellor'];
  const languageOptions = ['English', 'Hindi', 'Marathi', 'Bengali', 'Tamil', 'Telugu', 'Kannada', 'Gujarati'];
  const modeOptions = ['Online', 'Offline', 'Both'];
  const genderOptions = ['Male', 'Female', 'Other'];
  const availabilityTypes = ['Weekday', 'Weekend', 'Flexible'];

  // Form State
  const [profileForm, setProfileForm] = useState({
    contact_person: '',
    organization_name: '',
    mobile: '',
    bio: '',
    location: '',
    website: '',
    experience_years: 0,
    expertise: '',
    // ⭐ NEW FIELDS ADDED
    category: '',
    gender: '',
    mode: '',
    languages: [],
    availability_type: '',
    pricing: { "Per Hour": 0, "Per Session": 0, "Per Week": 0, "Per Month": 0, "One-time Fee": 0, "Yearly Subscription": 0 }
  });

  const [newSlot, setNewSlot] = useState({ day: 'Monday', start_time: '', end_time: '' });
  const [publicationRequest, setPublicationRequest] = useState('');

  // --- Initialization ---
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // 1. Get Profile
      const profileRes = await counselingApi.getCounselorProfile();
      const userData = profileRes.data || profileRes.counselor;

      if (userData) {
        const defaultPricing = {
          "Per Hour": userData.consultation_fee || 0,
          "Per Session": 0, "Per Week": 0, "Per Month": 0, "One-time Fee": 0, "Yearly Subscription": 0
        };
        const finalPricing = { ...defaultPricing, ...(userData.pricing || {}) };

        setProfile({ ...userData, pricing: finalPricing });

        setProfileForm({
          contact_person: userData.contact_person || '',
          organization_name: userData.organization_name || '',
          mobile: userData.mobile || '',
          bio: userData.bio || '',
          location: userData.location || '',
          website: userData.website || '',
          experience_years: userData.experience_years || 0,
          expertise: userData.expertise ? userData.expertise.join(', ') : '',
          // ⭐ Load new fields
          category: userData.category || '',
          gender: userData.gender || '',
          mode: userData.mode || '',
          languages: userData.languages || [],
          availability_type: userData.availability_type || '',
          pricing: finalPricing,
        });
      }

      // 2. Get Related Data
      const [bookingsRes, statusRes, availRes, acceptRes] = await Promise.allSettled([
        counselingApi.getCounselorBookings(),
        counselingApi.getPublicationStatus(),
        counselingApi.getAvailability(),
        counselingApi.getAcceptingStatus(),
      ]);

      if (bookingsRes.status === 'fulfilled') {
        const bookingList = bookingsRes.value.bookings || [];
        setBookings(bookingList);
        
        const completed = bookingList.filter(b => b.status === 'completed');
        const totalEarned = completed.reduce((sum, b) => sum + (b.amount || 0), 0);
        
        setCompletedSessions(completed.length);
        setEarnings(totalEarned);
      }

      if (statusRes.status === 'fulfilled') setPublicationStatus(statusRes.value.data || statusRes.value);
      if (availRes.status === 'fulfilled') setAvailability(availRes.value.available_slots || []);
      if (acceptRes.status === 'fulfilled') setAccepting(acceptRes.value.accepting_bookings || false);

    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // --- Handlers ---

  const handleLogout = () => {
    if(window.confirm("Are you sure you want to logout?")) {
      localStorage.clear();
      document.cookie.split(";").forEach((c) => {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      window.location.href = '/login?type=partner';
    }
  };

  const handlePaymentRedirect = () => {
    navigate('/counselor/checkout', {
      state: { formData: profile }
    });
  };

  const handlePricingChange = (type, value) => {
    setProfileForm(prev => ({
      ...prev,
      pricing: { ...prev.pricing, [type]: parseInt(value) || 0 }
    }));
  };

  // New Handler for Languages
  const handleLanguageToggle = (lang) => {
    setProfileForm(prev => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang]
    }));
  };

  const handleProfileUpdate = async () => {
    setSaving(true);
    try {
      const expertiseArray = profileForm.expertise.split(',').map(s => s.trim()).filter(s => s !== '');
      const payload = { 
        ...profileForm, 
        experience_years: parseInt(profileForm.experience_years), 
        pricing: profileForm.pricing, 
        expertise: expertiseArray 
      };
      await counselingApi.updateCounselorProfile(payload);
      alert('Profile & settings updated successfully!');
      setEditMode(false);
      loadDashboardData(); 
    } catch (error) {
      alert('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSaving(true);
    try {
      await counselingApi.uploadCounselorPhoto(file);
      alert('Photo uploaded successfully');
      loadDashboardData();
    } catch (error) {
      alert('Failed to upload photo');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAccepting = async () => {
    const newState = !accepting;
    setAccepting(newState); 
    try {
      await counselingApi.toggleAcceptingBookings(newState);
    } catch (error) {
      setAccepting(!newState);
      alert('Failed to update status');
    }
  };

  const handleAddSlot = async () => {
    if (!newSlot.start_time || !newSlot.end_time) return alert('Please fill in all time fields');
    setSaving(true);
    const slotWithId = { ...newSlot, id: Date.now().toString() };
    const updatedSlots = [...availability, slotWithId];
    try {
      await counselingApi.updateAvailability(updatedSlots);
      setAvailability(updatedSlots);
      setNewSlot({ ...newSlot, start_time: '', end_time: '' });
    } catch (error) {
      alert('Failed to add slot');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSlot = async (index) => {
    if(!window.confirm("Delete this slot?")) return;
    const updatedSlots = availability.filter((_, i) => i !== index);
    try {
      await counselingApi.updateAvailability(updatedSlots);
      setAvailability(updatedSlots);
    } catch (error) {
      alert('Failed to remove slot');
    }
  };

  const handleRequestPublication = async () => {
    if (!publicationRequest.trim()) return alert('Please enter a message for the admin');
    setSaving(true);
    try {
      const res = await counselingApi.requestPublication({ message: publicationRequest });
      if (res.success) {
        alert('Publication request submitted!');
        loadDashboardData();
      } else {
        alert(res.message || 'Failed to submit request');
      }
    } catch (error) {
      alert('Error submitting request');
    } finally {
      setSaving(false);
    }
  };

  const handleBookingAction = async (id, status) => {
    if(!window.confirm(`Mark this booking as ${status}?`)) return;
    try {
        await counselingApi.updateBookingStatus(id, { status });
        loadDashboardData();
    } catch (error) {
        alert('Action failed');
    }
  };

  if (loading && !profile) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-indigo-600'}`}>
        <div className="flex flex-col items-center">
          <Loader2 className="w-10 h-10 animate-spin mb-4" />
          <p>Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  // --- Render Sections ---

  const renderProfile = () => (
    <div className={`rounded-xl shadow-sm border p-4 md:p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className={`text-lg md:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Your Public Profile</h3>
        <button
          onClick={() => setEditMode(!editMode)}
          className="flex items-center space-x-2 text-indigo-600 font-medium hover:bg-indigo-50 dark:hover:bg-indigo-900/30 px-3 py-1 rounded transition"
        >
          <Edit className="w-4 h-4" />
          <span>{editMode ? 'Cancel' : 'Edit'}</span>
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        <div className="flex flex-col items-center space-y-4">
          <div className="relative group w-32 h-32">
            <img
              src={profile?.photo_url || `https://ui-avatars.com/api/?name=${profile?.contact_person}&background=random`}
              alt="Profile"
              className={`w-full h-full rounded-full object-cover border-4 shadow-md ${isDarkMode ? 'border-gray-700' : 'border-indigo-100'}`}
            />
            <label className="absolute bottom-0 right-0 bg-indigo-600 text-white p-2 rounded-full cursor-pointer hover:bg-indigo-700 shadow transition transform hover:scale-110">
              {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4" />}
              <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" disabled={saving} />
            </label>
          </div>
          {!editMode && (
             <div className="text-center space-y-2">
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase block ${profile?.published ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'}`}>
                    {profile?.published ? 'Live' : 'Hidden'}
                </span>
             </div>
          )}
        </div>

        <div className="flex-1">
          {editMode ? (
            <div className="space-y-6 animate-in fade-in">
              {/* Basic Info Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {[
                  { label: 'Full Name', key: 'contact_person', type: 'text' },
                  { label: 'Organization / Title', key: 'organization_name', type: 'text' },
                  { label: 'Years of Experience', key: 'experience_years', type: 'number' },
                  { label: 'Location (City)', key: 'location', type: 'text' },
                  { label: 'Website', key: 'website', type: 'text' },
                  { label: 'Skills (Comma separated)', key: 'expertise', type: 'text' },
                ].map((field) => (
                  <div key={field.key}>
                    <label className={`text-xs font-bold uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{field.label}</label>
                    <input
                      type={field.type}
                      className={`w-full mt-1 p-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      value={profileForm[field.key]}
                      onChange={e => setProfileForm({...profileForm, [field.key]: e.target.value})}
                    />
                  </div>
                ))}

                {/* ⭐ NEW EDITABLE FIELDS */}
                <div>
                  <label className={`text-xs font-bold uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Category</label>
                  <select
                    className={`w-full mt-1 p-3 border rounded-lg outline-none ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    value={profileForm.category}
                    onChange={e => setProfileForm({...profileForm, category: e.target.value})}
                  >
                    <option value="">Select Category</option>
                    {categoryOptions.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>

                <div>
                  <label className={`text-xs font-bold uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Gender</label>
                  <select
                    className={`w-full mt-1 p-3 border rounded-lg outline-none ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    value={profileForm.gender}
                    onChange={e => setProfileForm({...profileForm, gender: e.target.value})}
                  >
                    <option value="">Select Gender</option>
                    {genderOptions.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>

                <div>
                  <label className={`text-xs font-bold uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Mode of Session</label>
                  <select
                    className={`w-full mt-1 p-3 border rounded-lg outline-none ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    value={profileForm.mode}
                    onChange={e => setProfileForm({...profileForm, mode: e.target.value})}
                  >
                    <option value="">Select Mode</option>
                    {modeOptions.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>

                <div>
                  <label className={`text-xs font-bold uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Availability Type</label>
                  <select
                    className={`w-full mt-1 p-3 border rounded-lg outline-none ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    value={profileForm.availability_type}
                    onChange={e => setProfileForm({...profileForm, availability_type: e.target.value})}
                  >
                    <option value="">Select Type</option>
                    {availabilityTypes.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              
              {/* Languages Toggle */}
              <div>
                <label className={`text-xs font-bold uppercase block mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Languages Spoken</label>
                <div className="flex flex-wrap gap-2">
                  {languageOptions.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => handleLanguageToggle(lang)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                        profileForm.languages.includes(lang)
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : isDarkMode 
                            ? 'bg-gray-700 text-gray-300 border-gray-600'
                            : 'bg-white text-gray-600 border-gray-300'
                      }`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pricing Section */}
              <div className={`p-5 rounded-lg border ${isDarkMode ? 'bg-gray-700/30 border-gray-600' : 'bg-indigo-50/50 border-indigo-100'}`}>
                <h4 className={`font-bold mb-4 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    <DollarSign className="w-4 h-4 mr-1 text-green-500"/> Service Rates (₹)
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pricingTypes.map(type => (
                        <div key={type}>
                            <label className={`text-xs font-bold uppercase mb-1 block ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{type}</label>
                            <div className="relative">
                                <span className={`absolute left-3 top-2.5 font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>₹</span>
                                <input
                                    type="number"
                                    placeholder="0"
                                    className={`w-full pl-8 p-2 border rounded focus:ring-2 focus:ring-indigo-500 outline-none ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                    value={profileForm.pricing?.[type] || ''}
                                    onChange={(e) => handlePricingChange(type, e.target.value)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className={`text-xs font-bold uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Bio / About You</label>
                <textarea
                  rows="4"
                  className={`w-full mt-1 p-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  value={profileForm.bio}
                  onChange={e => setProfileForm({...profileForm, bio: e.target.value})}
                />
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleProfileUpdate}
                  disabled={saving}
                  className={`bg-indigo-600 text-white px-8 py-3 rounded-lg hover:bg-indigo-700 w-full md:w-auto font-bold transition shadow-md flex items-center justify-center gap-2 ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin"/> : <CheckCircle className="w-5 h-5"/>}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-8 animate-in fade-in">
              <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                <div>
                    <h2 className={`text-2xl md:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{profile?.contact_person}</h2>
                    <p className="text-indigo-500 font-medium text-lg">{profile?.organization_name}</p>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{profile?.category}</p>
                </div>
              </div>

              {/* Pricing Display */}
              <div>
                 <h4 className={`text-xs font-bold uppercase mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Service Rates</h4>
                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {profile?.pricing && Object.entries(profile.pricing).map(([type, price]) => (
                        <div 
                          key={type} 
                          className={`px-4 py-3 rounded-lg border flex justify-between items-center transition hover:shadow-sm ${
                            price > 0 
                              ? (isDarkMode ? 'bg-indigo-900/20 border-indigo-500/50' : 'bg-white border-indigo-200 shadow-sm') 
                              : (isDarkMode ? 'bg-gray-800 border-gray-700 opacity-50' : 'bg-gray-50 border-gray-200 opacity-60')
                          }`}
                        >
                            <span className={`text-xs font-bold uppercase ${price > 0 ? 'text-indigo-600 dark:text-indigo-300' : 'text-gray-500'}`}>{type}</span>
                            <span className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {price > 0 ? `₹${price}` : '-'}
                            </span>
                        </div>
                    ))}
                 </div>
              </div>
              
              {/* Expertise Tags */}
              <div className="flex flex-wrap gap-2">
                {profile?.expertise?.map((tag, i) => (
                    <span key={i} className={`px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-indigo-50 text-indigo-700 border border-indigo-100'}`}>
                        {tag}
                    </span>
                ))}
              </div>

              {/* Languages Tags */}
               {profile?.languages?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {profile.languages.map((lang, i) => (
                    <span key={i} className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${isDarkMode ? 'bg-green-900/30 text-green-300 border border-green-800' : 'bg-green-50 text-green-700 border border-green-100'}`}>
                       <Languages className="w-3 h-3"/> {lang}
                    </span>
                  ))}
                </div>
              )}

              {/* Details Grid */}
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <div className="flex items-center"><Briefcase className="w-4 h-4 mr-2 opacity-70"/> {profile?.experience_years} Years Exp.</div>
                <div className="flex items-center"><MapPin className="w-4 h-4 mr-2 opacity-70"/> {profile?.location || 'Location not set'}</div>
                <div className="flex items-center"><Monitor className="w-4 h-4 mr-2 opacity-70"/> {profile?.mode || 'Not specified'}</div>
                <div className="flex items-center"><Clock className="w-4 h-4 mr-2 opacity-70"/> {profile?.availability_type || 'Flexible'}</div>
                <div className="flex items-center"><Mail className="w-4 h-4 mr-2 opacity-70"/> {profile?.email}</div>
                <div className="flex items-center"><Phone className="w-4 h-4 mr-2 opacity-70"/> {profile?.mobile}</div>
                <div className="flex items-center"><Users className="w-4 h-4 mr-2 opacity-70"/> {profile?.gender}</div>
              </div>
              
              {/* Bio */}
              <div className={`p-6 rounded-lg leading-relaxed text-sm border ${isDarkMode ? 'bg-gray-700/30 border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-100 text-gray-700'}`}>
                {profile?.bio || "No bio added yet. Click edit to add your bio."}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderAvailability = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
       <div className="md:col-span-2 space-y-4">
           <h3 className={`font-bold text-xl ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Your Weekly Schedule</h3>
           {availability.map((slot, idx) => (
               <div key={idx} className={`p-4 rounded-lg shadow-sm border flex justify-between items-center ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
                   <div className="flex items-center space-x-4">
                       <span className="font-bold text-indigo-500 w-24">{slot.day}</span>
                       <span className={`px-3 py-1 rounded text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>{slot.start_time} - {slot.end_time}</span>
                   </div>
                   <button onClick={() => handleRemoveSlot(idx)} className="text-red-400 hover:text-red-600">
                       <XCircle className="w-5 h-5"/>
                   </button>
               </div>
           ))}
           {availability.length === 0 && (
               <div className={`text-center p-8 rounded-lg border border-dashed ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-white border-gray-200 text-gray-400'}`}>
                   No slots added yet.
               </div>
           )}
       </div>
       <div className={`p-6 rounded-xl shadow-sm h-fit ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
           <h3 className={`font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Add New Slot</h3>
           <div className="space-y-3">
               <select className={`w-full p-2 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} value={newSlot.day} onChange={e => setNewSlot({...newSlot, day: e.target.value})}>
                   {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(d => <option key={d} value={d}>{d}</option>)}
               </select>
               <div className="flex gap-2">
                   <input type="time" className={`w-full p-2 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} value={newSlot.start_time} onChange={e => setNewSlot({...newSlot, start_time: e.target.value})}/>
                   <input type="time" className={`w-full p-2 border rounded ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} value={newSlot.end_time} onChange={e => setNewSlot({...newSlot, end_time: e.target.value})}/>
               </div>
               <button 
                   onClick={handleAddSlot} 
                   disabled={saving}
                   className="w-full bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700 transition flex items-center justify-center gap-2"
               >
                   {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : 'Add Slot'}
               </button>
           </div>
       </div>
    </div>
  );

  const renderBookings = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
         <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Upcoming Sessions</h3>
         <span className={`px-3 py-1 rounded-full text-sm font-medium ${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>{bookings.length} Total</span>
      </div>

      {bookings.length === 0 ? (
        <div className={`text-center py-12 rounded-xl border border-dashed ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <Calendar className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No bookings found</p>
        </div>
      ) : (
        bookings.map((booking) => (
          <div key={booking._id} className={`p-6 rounded-xl shadow-sm border hover:shadow-md transition ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <div className="flex items-center space-x-2">
                    <h4 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{booking.student_name || "Student"}</h4>
                    <span className={`px-2 py-0.5 rounded text-xs uppercase font-bold ${
                        booking.status === 'confirmed' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 
                        booking.status === 'completed' ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' :
                        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                    }`}>
                        {booking.status}
                    </span>
                </div>
                <div className={`text-sm mt-1 flex items-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Calendar className="w-3 h-3 mr-1"/> {new Date(booking.created_at).toLocaleDateString()} 
                    <span className="mx-2">•</span>
                    <Clock className="w-3 h-3 mr-1"/> {booking.slot_details || "Scheduled Slot"}
                </div>
              </div>

              <div className="mt-4 md:mt-0 flex space-x-2">
                {booking.status === 'pending' && (
                    <button onClick={() => handleBookingAction(booking._id, 'confirmed')} className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700">Accept</button>
                )}
                {booking.status === 'confirmed' && (
                    <button onClick={() => handleBookingAction(booking._id, 'completed')} className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">Mark Complete</button>
                )}
                {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                    <button onClick={() => handleBookingAction(booking._id, 'cancelled')} className={`border px-4 py-2 rounded text-sm ${isDarkMode ? 'border-red-800 text-red-400 hover:bg-red-900/30' : 'border-red-200 text-red-600 hover:bg-red-50'}`}>Cancel</button>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderPublication = () => (
    <div className={`rounded-xl shadow-sm border p-8 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
      <div className="max-w-3xl mx-auto">
        <div className={`mb-8 p-4 rounded-lg flex items-center gap-4 ${
            publicationStatus?.publication_status === 'approved' ? 'bg-green-500/10 border border-green-500/20' : 
            publicationStatus?.publication_status === 'rejected' ? 'bg-red-500/10 border border-red-500/20' : 
            'bg-yellow-500/10 border border-yellow-500/20'
        }`}>
            <div className={`p-2 rounded-full ${
                publicationStatus?.publication_status === 'approved' ? 'bg-green-100 text-green-600' : 
                publicationStatus?.publication_status === 'rejected' ? 'bg-red-100 text-red-600' : 
                'bg-yellow-100 text-yellow-600'
            }`}>
                 {publicationStatus?.publication_status === 'approved' ? <CheckCircle className="w-6 h-6"/> : 
                  publicationStatus?.publication_status === 'rejected' ? <XCircle className="w-6 h-6"/> : 
                  <AlertCircle className="w-6 h-6"/>}
            </div>
            <div>
                <h3 className={`font-bold text-lg capitalize ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Status: {publicationStatus?.publication_status?.replace('_', ' ') || 'Unknown'}
                </h3>
                {publicationStatus?.rejection_reason && (
                    <p className="text-red-500 text-sm mt-1">Reason: {publicationStatus.rejection_reason}</p>
                )}
            </div>
        </div>

        <div className="mb-8">
            <h3 className={`text-lg font-bold mb-4 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                <Award className="w-5 h-5 mr-2 text-indigo-500"/> What Admin Will See
            </h3>
            <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <p className="text-xs font-bold text-indigo-500 uppercase mb-1">Organization</p>
                        <p className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{profile?.organization_name || "Not Set"}</p>
                    </div>
                    <div>
                        <p className="text-xs font-bold text-indigo-500 uppercase mb-1">Experience</p>
                        <p className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{profile?.experience_years} Years</p>
                    </div>
                    <div className="md:col-span-2">
                        <p className="text-xs font-bold text-indigo-500 uppercase mb-1">Active Fees</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                            {profile?.pricing && Object.entries(profile.pricing).map(([k,v]) => (
                                v > 0 && <span key={k} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded border border-green-200">{k}: ₹{v}</span>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="text-xs font-bold text-indigo-500 uppercase mb-1">Availability</p>
                        <p className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{availability.length} Slots Active</p>
                    </div>
                    <div className="md:col-span-2">
                        <p className="text-xs font-bold text-indigo-500 uppercase mb-1">Expertise</p>
                        <p className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                            {profile?.expertise?.length > 0 ? profile.expertise.join(', ') : "Not Set"}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        {publicationStatus?.publication_status !== 'pending' && (
            <div className={`p-6 rounded-xl text-left border ${isDarkMode ? 'bg-indigo-900/20 border-indigo-800' : 'bg-indigo-50 border-indigo-100'}`}>
                <h3 className={`font-bold text-lg mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Request Publication</h3>
                <textarea 
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
                    rows="3"
                    placeholder="Message to Admin (e.g. Updated prices and slots)..."
                    value={publicationRequest}
                    onChange={(e) => setPublicationRequest(e.target.value)}
                ></textarea>
                <button 
                    onClick={handleRequestPublication}
                    className="w-full mt-4 bg-indigo-600 text-white py-3 rounded-lg font-bold hover:bg-indigo-700 transition flex items-center justify-center gap-2"
                >
                    <Send className="w-4 h-4" /> Submit for Review
                </button>
            </div>
        )}
      </div>
    </div>
  );

  // --- Main Layout ---
  const isRegistrationIncomplete = 
  profile?.registration_status === 'incomplete' || 
  profile?.registration_status === 'pending_payment' ||
  profile?.registration_status === 'draft_saved';
        const handleCompleteRegistration = () => {
      navigate('/counselor/register');
    };

  return (
    <div className={`min-h-screen pt-28 flex flex-col transition-colors duration-200 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      
      {/* HEADER */}
      <div className={`w-full px-6 py-8 shadow-sm transition-colors ${isDarkMode ? 'bg-gray-800 border-b border-gray-700' : 'bg-gradient-to-r from-indigo-900 to-purple-900 text-white'}`}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-white'}`}>Counselor Dashboard</h1>
            <p className={`mt-1 text-base ${isDarkMode ? 'text-gray-400' : 'text-indigo-200'}`}>Welcome back, {profile?.contact_person}</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Booking Toggle */}
            <div 
              onClick={handleToggleAccepting}
              className={`cursor-pointer px-5 py-3 rounded-full flex items-center gap-3 transition-all shadow-md border hover:scale-105 ${
                accepting 
                ? (isDarkMode ? 'bg-green-900/30 border-green-700 text-green-400' : 'bg-white/20 border-white/30 text-white') 
                : (isDarkMode ? 'bg-red-900/30 border-red-700 text-red-400' : 'bg-white/10 border-white/20 text-indigo-100')
              }`}
            >
              {accepting ? <ToggleRight className="w-8 h-8"/> : <ToggleLeft className="w-8 h-8 opacity-70"/>}
              <div className="hidden sm:flex flex-col text-left">
                  <span className="font-bold text-sm leading-tight">{accepting ? 'Active' : 'Paused'}</span>
                  <span className="text-xs opacity-80">{accepting ? 'Visible' : 'Hidden'}</span>
              </div>
            </div>

            {/* LOGOUT BUTTON */}
            <button 
              onClick={handleLogout}
              className="bg-red-500/20 hover:bg-red-500/40 border border-red-400/30 text-white p-3 rounded-full transition"
              title="Logout"
            >
              <LogOut className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 py-8 pb-20">
        
        {/* ⚠️ REGISTRATION ALERT BANNER */}
        {isRegistrationIncomplete && (
          <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg shadow-md mb-8 flex flex-col md:flex-row justify-between items-center gap-4 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-bold text-red-800">Registration Incomplete</h3>
                <p className="text-red-700">
  {profile?.registration_status === 'pending_payment'
    ? "Payment pending hai. Please complete payment to activate account."
    : "Profile incomplete hai. Please complete your details."}
</p>              </div>
            </div>
            {profile?.registration_status === 'pending_payment' ? (
  <button 
    onClick={handlePaymentRedirect}
    className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-bold shadow-lg transition"  >
    Pay Now
  </button>
) : (
  <button 
    onClick={handleCompleteRegistration}
    className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold"
  >
    Complete Profile
  </button>
)}
          </div>
        )}

        {/* Navigation Tabs */}
        <div className={`rounded-xl shadow-md p-2 mb-8 flex overflow-x-auto scrollbar-hide border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'profile', label: 'Profile' },
              { id: 'bookings', label: 'Bookings' },
              { id: 'availability', label: 'Schedule' },
              { id: 'publication', label: 'Publication' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 min-w-[100px] py-3 rounded-lg text-sm font-semibold capitalize transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? (isDarkMode ? 'bg-indigo-600 text-white shadow-md' : 'bg-indigo-50 text-indigo-700 shadow-sm')
                    : (isDarkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-white' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700')
                }`}
              >
                {tab.label}
              </button>
            ))}
        </div>

        {/* Dynamic Tab Content */}
        <div className="transition-all duration-300">
          {activeTab === 'overview' && (
             <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* CARD 1: TOTAL EARNINGS */}
                    <div className={`p-6 rounded-xl shadow-sm border-l-4 border-green-500 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                        <div className="flex justify-between items-start">
                            <div>
                                <p className={`text-sm font-bold uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Earnings</p>
                                <p className={`text-3xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>₹{earnings}</p>
                            </div>
                            <div className="p-3 bg-green-100 rounded-full">
                                <Wallet className="w-6 h-6 text-green-600" />
                            </div>
                        </div>
                    </div>

                    {/* CARD 2: COMPLETED SESSIONS */}
                    <div className={`p-6 rounded-xl shadow-sm border-l-4 border-indigo-500 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                        <div className="flex justify-between items-start">
                            <div>
                                <p className={`text-sm font-bold uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Sessions Completed</p>
                                <p className={`text-3xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{completedSessions}</p>
                            </div>
                            <div className="p-3 bg-indigo-100 rounded-full">
                                <CheckCircle className="w-6 h-6 text-indigo-600" />
                            </div>
                        </div>
                    </div>

                    {/* CARD 3: STATUS */}
                    <div className={`p-6 rounded-xl shadow-sm border-l-4 border-purple-500 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                        <div className="flex justify-between items-start">
                            <div>
                                <p className={`text-sm font-bold uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Profile Status</p>
                                <p className={`text-xl font-bold mt-2 capitalize ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                    {publicationStatus?.publication_status?.replace('_', ' ')}
                                </p>
                            </div>
                            <div className="p-3 bg-purple-100 rounded-full">
                                <TrendingUp className="w-6 h-6 text-purple-600" />
                            </div>
                        </div>
                    </div>
                </div>
                {renderProfile()}
             </div>
          )}
          
          {activeTab === 'profile' && renderProfile()}
          {activeTab === 'availability' && renderAvailability()}
          {activeTab === 'bookings' && renderBookings()}
          {activeTab === 'publication' && renderPublication()}
        </div>
      </div>
    </div>
  );
};

export default CounselorDashboard;